
import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthProvider';
import { baseUrl_SPB } from '../BaseUrl';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Button, TextField, Stack, Alert, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { Drawer } from 'antd';
import { v4 as uuidv4 } from 'uuid';

const CopyButton = ({ value }) => {
  const [copied, setCopied] = React.useState(false);
  if (!value) return null;
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5, padding: '2px' }}>
        <ContentCopyIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
};

const CustomerManagement = () => {
  const authContext = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editError, setEditError] = useState(null);
  const [editSuccess, setEditSuccess] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${baseUrl_SPB}/api/v1/businesscustomers/getcustomers`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authContext.token,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch customers');
      let data = await response.json();
      data = data.map(item => ({ ...item, key: item.id || uuidv4() }));
      setCustomers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
     if(authContext?.token)
      fetchCustomers();
    // eslint-disable-next-line
  }, [authContext.token]);

  // Open edit drawer
  const handleEdit = (item) => {
    setEditItem({ ...item });
    setEditError(null);
    setEditSuccess(null);
    setEditOpen(true);
  };

  // Handle edit form change
  const handleEditChange = (e) => {
    setEditItem({ ...editItem, [e.target.name]: e.target.value });
  };

  // Validate edit form
  const validateEdit = () => {
    if (!editItem.firstName) return 'First name is mandatory';
    if (!editItem.lastName) return 'Last name is mandatory';
    if (!editItem.email) return 'Email is mandatory';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editItem.email)) return 'Invalid email format';
    if (editItem.abn && !/^\d{11}$/.test(editItem.abn)) return 'Invalid ABN format. It should be 11 digits';
    if (editItem.businessName && /\d/.test(editItem.businessName)) return 'Business name should not contain numbers';
    return null;
  };

  // Save edit
  const handleEditSave = async () => {
    setEditError(null);
    setEditSuccess(null);
    const errMsg = validateEdit();
    if (errMsg) {
      setEditError(errMsg);
      return;
    }
    setEditLoading(true);
    try {
      const response = await fetch(`${baseUrl_SPB}/api/v1/businesscustomers/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authContext.token,
        },
        body: JSON.stringify(editItem),
      });
      if (!response.ok) throw new Error('Failed to update customer');
      setEditSuccess('Customer updated successfully!');
      setEditOpen(false);
      fetchCustomers();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  // Check if user can delete
  const canDelete = authContext?.role === 'ROLE_MANAGER' || authContext?.role === 'ROLE_CMANAGER';

  // Handle delete
  const handleDelete = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (!deleteItem) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(`${baseUrl_SPB}/api/v1/businesscustomers/delete?id=${deleteItem.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authContext.token,
        },
      });
      if (!response.ok) throw new Error('Failed to delete customer');
      setDeleteDialogOpen(false);
      setDeleteItem(null);
      fetchCustomers();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Paper sx={{ padding: 2 }}>
      <Typography variant="h5" gutterBottom>Customer Management</Typography>
      {loading ? <CircularProgress /> : error ? <Typography color="error">{error}</Typography> : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer ID</TableCell>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Business Name</TableCell>
                <TableCell>ABN</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Medical History Notes</TableCell>
                <TableCell>Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map(item => (
                <TableRow key={item.key}>
                  <TableCell>
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.id || item.customerId || '—'}</span>
                    <CopyButton value={item.id || item.customerId} />
                  </TableCell>
                  <TableCell>
                    {item.firstName}
                    <CopyButton value={item.firstName ? `${item.firstName} ${item.lastName || ''}`.trim() : null} />
                  </TableCell>
                  <TableCell>
                    {item.lastName}
                  </TableCell>
                  <TableCell>
                    {item.email}
                    <CopyButton value={item.email} />
                  </TableCell>
                  <TableCell>
                    {item.phoneNumber}
                    <CopyButton value={item.phoneNumber} />
                  </TableCell>
                  <TableCell>{item.businessName}</TableCell>
                  <TableCell>{item.abn}</TableCell>
                  <TableCell>{item.address}</TableCell>
                  <TableCell>{item.notes}</TableCell>
                  <TableCell>{item.medicalHistoryNotes}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" size="small" onClick={() => handleEdit(item)}>Edit</Button>
                      {canDelete && (
                        <Tooltip title="Delete customer">
                          <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Button variant="contained" color="primary" href="/customer/add" sx={{ mt: 2 }}>Add New Customer</Button>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete {deleteItem?.firstName} {deleteItem?.lastName}? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Drawer */}
      <Drawer
        title="Edit Customer"
        placement="right"
        open={editOpen}
        onClose={() => setEditOpen(false)}
        width={420}
        destroyOnClose
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          {editError && <Alert severity="error">{editError}</Alert>}
          {editSuccess && <Alert severity="success">{editSuccess}</Alert>}
          {editItem && (
            <>
              <TextField label="First Name" name="firstName" value={editItem.firstName || ''} onChange={handleEditChange} required fullWidth />
              <TextField label="Last Name" name="lastName" value={editItem.lastName || ''} onChange={handleEditChange} required fullWidth />
              <TextField label="Email" name="email" value={editItem.email || ''} onChange={handleEditChange} required fullWidth />
              <TextField label="Phone Number" name="phoneNumber" value={editItem.phoneNumber || ''} onChange={handleEditChange} fullWidth />
              <TextField label="Business Name" name="businessName" value={editItem.businessName || ''} onChange={handleEditChange} fullWidth />
              <TextField label="ABN" name="abn" value={editItem.abn || ''} onChange={handleEditChange} fullWidth />
              <TextField label="Address" name="address" value={editItem.address || ''} onChange={handleEditChange} fullWidth />
              <TextField label="Notes" name="notes" value={editItem.notes || ''} onChange={handleEditChange} fullWidth />
              <TextField label="Medical History Notes" name="medicalHistoryNotes" value={editItem.medicalHistoryNotes || ''} onChange={handleEditChange} fullWidth />
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button onClick={() => setEditOpen(false)} color="secondary" variant="outlined">Cancel</Button>
                <Button onClick={handleEditSave} variant="contained" color="primary" disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</Button>
              </Stack>
            </>
          )}
        </Stack>
      </Drawer>
    </Paper>
  );
};

export default CustomerManagement;
